import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  User,
  Phone,
  Sparkles,
  PlusCircle,
  AlertCircle,
  CheckCircle2,
  CalendarDays,
  Info,
  Sun,
  Moon
} from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function AvailabilityCalendar({
  onSelectDateToBook,
  onViewBooking,
  refreshKey
}) {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1); // 1-12
  const [monthData, setMonthData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Selected date inspection
  const todayStr = today.toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState(todayStr);

  const fetchMonthSummary = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/calendar/month-summary`, {
        params: { year: currentYear, month: currentMonth }
      });
      setMonthData(res.data.days || {});
    } catch (err) {
      console.error("Failed to fetch calendar summary:", err);
    } finally {
      setIsLoading(false);
    }
  }, [currentYear, currentMonth]);

  useEffect(() => {
    fetchMonthSummary();
  }, [fetchMonthSummary, refreshKey]);

  const handlePrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const handleToday = () => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth() + 1);
    setSelectedDate(todayStr);
  };

  // Build Calendar Matrix for Current Month
  const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1).getDay(); // 0 is Sun
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();

  const calendarDays = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const mm = String(currentMonth).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    calendarDays.push(`${currentYear}-${mm}-${dd}`);
  }

  // Selected date details
  const selectedInfo = monthData[selectedDate] || {
    status: "available",
    day_booked: false,
    night_booked: false,
    bookings: []
  };

  const daySlotFree = !selectedInfo.day_booked;
  const nightSlotFree = !selectedInfo.night_booked;

  return (
    <div data-testid={TEST_IDS.calendarContainer} className="space-y-6">
      
      {/* Month Header Banner with Short Colour Guide */}
      <div className="bg-white p-5 sm:p-6 rounded-3xl border border-pink-100 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-pink-900 to-rose-700 text-white flex items-center justify-center shadow-md">
            <CalendarIcon className="w-6 h-6" />
          </div>
          <div>
            <h1
              data-testid={TEST_IDS.calendarMonthTitle}
              className="text-2xl font-black text-pink-950 tracking-tight"
            >
              {MONTH_NAMES[currentMonth - 1]} {currentYear}
            </h1>
            <p className="text-xs text-gray-500 font-medium">
              Tap any date to inspect Day/Night bookings and reserve convention hall slots
            </p>
          </div>
        </div>

        {/* Legend & Navigation */}
        <div className="flex items-center flex-wrap gap-2.5 sm:gap-3">
          {/* SHORT COLOUR GUIDE */}
          <div className="flex items-center flex-wrap gap-2 sm:gap-3 px-3.5 py-2 bg-pink-50/80 border border-pink-200 rounded-2xl text-[11px] sm:text-xs font-bold shadow-2xs">
            <div className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 rounded-md bg-emerald-400 border border-emerald-600 shadow-xs inline-block" />
              <span className="text-emerald-950">Free (Both Open)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 rounded-md bg-amber-300 border border-amber-500 shadow-xs inline-block" />
              <span className="text-amber-950">1 Slot Booked</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 rounded-md bg-rose-500 border border-rose-700 shadow-xs inline-block" />
              <span className="text-rose-950">Fully Booked</span>
            </div>
          </div>

          <div className="flex items-center gap-1 bg-pink-50 p-1 rounded-xl border border-pink-200">
            <button
              data-testid={TEST_IDS.calendarPrevMonthBtn}
              onClick={handlePrevMonth}
              className="p-1.5 hover:bg-white text-pink-900 rounded-lg transition-colors"
              title="Previous Month"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={handleToday}
              className="px-3 py-1 text-xs font-bold text-pink-900 hover:bg-white rounded-lg transition-colors"
            >
              Today
            </button>
            <button
              data-testid={TEST_IDS.calendarNextMonthBtn}
              onClick={handleNextMonth}
              className="p-1.5 hover:bg-white text-pink-900 rounded-lg transition-colors"
              title="Next Month"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Grid: Calendar on Left (8 cols), Selected Date Drawer on Right (4 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* CALENDAR VIEW */}
        <div className="lg:col-span-8 bg-white p-5 sm:p-6 rounded-3xl border border-pink-100 shadow-sm space-y-4">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-2 text-center">
            {WEEKDAYS.map((w, idx) => (
              <div
                key={w}
                className={`py-1 text-xs font-black uppercase tracking-wider ${
                  idx === 0 || idx === 6 ? "text-pink-700 font-black" : "text-gray-500"
                }`}
              >
                {w}
              </div>
            ))}
          </div>

          {/* Month day cells */}
          <div className="grid grid-cols-7 gap-2 sm:gap-2.5">
            {calendarDays.map((dStr, index) => {
              if (!dStr) {
                return (
                  <div
                    key={`empty-${index}`}
                    className="min-h-[105px] sm:min-h-[125px] rounded-2xl bg-gray-50/40 border border-transparent"
                  />
                );
              }

              const dayNumber = parseInt(dStr.split("-")[2], 10);
              const isSelected = selectedDate === dStr;
              const isToday = dStr === todayStr;
              const info = monthData[dStr];
              
              const dayBooked = Boolean(info?.day_booked);
              const nightBooked = Boolean(info?.night_booked);

              // Distinct Slot-Based Color Coding & Text Labels
              let cardBg = "bg-emerald-50/90 hover:bg-emerald-100/90 border-emerald-300 text-emerald-950";
              let badgeBg = "bg-emerald-200/90 text-emerald-900 border-emerald-400";
              let statusLabel = "Free";

              if (dayBooked && nightBooked) {
                // Both slots booked -> Red
                cardBg = "bg-rose-100/95 hover:bg-rose-200/90 border-rose-400 text-rose-950";
                badgeBg = "bg-rose-600 text-white border-rose-700";
                statusLabel = "Fully booked";
              } else if (dayBooked) {
                // Day booked only -> Yellow
                cardBg = "bg-amber-100/90 hover:bg-amber-200/80 border-amber-400 text-amber-950";
                badgeBg = "bg-amber-500 text-white border-amber-600";
                statusLabel = "Day booked";
              } else if (nightBooked) {
                // Night booked only -> Yellow
                cardBg = "bg-amber-100/90 hover:bg-amber-200/80 border-amber-400 text-amber-950";
                badgeBg = "bg-amber-500 text-white border-amber-600";
                statusLabel = "Night booked";
              }

              // Highlight Selected Box
              if (isSelected) {
                cardBg += " ring-3 ring-pink-900 shadow-md scale-[1.02] font-black";
              }

              return (
                <div
                  key={dStr}
                  data-testid={TEST_IDS.calendarDayCell(dStr)}
                  onClick={() => setSelectedDate(dStr)}
                  className={`min-h-[105px] sm:min-h-[125px] p-2.5 sm:p-3 rounded-2xl border-2 cursor-pointer transition-all flex flex-col justify-between select-none shadow-2xs ${cardBg}`}
                >
                  {/* Top: Large Prominent Day Number & Today indicator */}
                  <div className="flex items-start justify-between">
                    <span
                      className={`text-lg sm:text-2xl font-black leading-none ${
                        isToday
                          ? "w-7 h-7 sm:w-8 sm:h-8 bg-pink-950 text-white rounded-full flex items-center justify-center shadow-xs"
                          : isSelected
                          ? "text-pink-950 font-black"
                          : "text-gray-900"
                      }`}
                    >
                      {dayNumber}
                    </span>

                    {isToday && (
                      <span className="text-[9px] font-extrabold uppercase tracking-wider text-pink-900 bg-pink-100/90 px-1.5 py-0.5 rounded-md">
                        Today
                      </span>
                    )}
                  </div>

                  {/* Middle: Client preview if booked */}
                  <div className="my-1 space-y-0.5 overflow-hidden">
                    {info?.bookings?.slice(0, 1).map((b, i) => (
                      <div
                        key={i}
                        className="truncate text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/80 text-gray-900 border border-black/10"
                        title={`${b.client_name} (${b.slot_type})`}
                      >
                        {b.client_name}
                      </div>
                    ))}
                  </div>

                  {/* Bottom: Explicit Slot Status Badge */}
                  <div className="pt-1 border-t border-black/5">
                    <span
                      className={`inline-block w-full text-center text-[10px] sm:text-[11px] font-extrabold px-1.5 py-0.5 rounded-lg border shadow-2xs ${badgeBg}`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* SELECTED DATE DETAIL DRAWER */}
        <div className="lg:col-span-4 bg-white p-5 sm:p-6 rounded-3xl border border-pink-100 shadow-sm space-y-5">
          <div className="flex items-center justify-between pb-3 border-b border-pink-100">
            <div>
              <span className="text-[10px] uppercase tracking-wider font-extrabold text-pink-700 block">
                Selected Date
              </span>
              <h2
                data-testid={TEST_IDS.selectedDateTitle}
                className="text-base sm:text-lg font-black text-pink-950"
              >
                {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "long",
                  day: "numeric",
                  year: "numeric"
                })}
              </h2>
            </div>

            <button
              data-testid={TEST_IDS.btnBookDate}
              onClick={() => onSelectDateToBook && onSelectDateToBook(selectedDate)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-pink-900 hover:bg-pink-950 text-white text-xs font-bold shadow transition-all"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Book Date</span>
            </button>
          </div>

          {/* Slot Breakdown Indicators */}
          <div className="space-y-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">
              Hall Slot Availability
            </span>
            <div className="grid grid-cols-2 gap-2">
              <div
                className={`p-3 rounded-2xl border flex items-center justify-between ${
                  daySlotFree
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                    : "bg-rose-50 border-rose-200 text-rose-900"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Sun className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-bold">Day Slot</span>
                </div>
                <span
                  data-testid="day-slot-status"
                  className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                    daySlotFree ? "bg-emerald-200 text-emerald-950" : "bg-rose-200 text-rose-950"
                  }`}
                >
                  {daySlotFree ? "Available" : "Booked"}
                </span>
              </div>

              <div
                className={`p-3 rounded-2xl border flex items-center justify-between ${
                  nightSlotFree
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                    : "bg-rose-50 border-rose-200 text-rose-900"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Moon className="w-4 h-4 text-indigo-600" />
                  <span className="text-xs font-bold">Night Slot</span>
                </div>
                <span
                  data-testid="night-slot-status"
                  className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                    nightSlotFree ? "bg-emerald-200 text-emerald-950" : "bg-rose-200 text-rose-950"
                  }`}
                >
                  {nightSlotFree ? "Available" : "Booked"}
                </span>
              </div>
            </div>
          </div>

          {/* Bookings on this date */}
          <div className="space-y-2 pt-2 border-t border-pink-100">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                Bookings on this Date ({selectedInfo.bookings?.length || 0})
              </span>
            </div>

            {selectedInfo.bookings?.length === 0 ? (
              <div className="p-6 rounded-2xl bg-pink-50/40 border border-pink-100 text-center space-y-1">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto" />
                <p className="text-xs font-bold text-pink-950">No bookings scheduled for this date.</p>
                <p className="text-[11px] text-gray-500">Day and Night slots are completely open.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {selectedInfo.bookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="p-3.5 rounded-2xl border border-pink-200 bg-pink-50/50 hover:bg-pink-100/60 transition-colors space-y-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="text-[10px] font-bold text-pink-800 uppercase tracking-wider bg-pink-200/80 px-1.5 py-0.5 rounded">
                          {booking.slot_type} Function
                        </span>
                        <h4 className="text-sm font-black text-pink-950 mt-1">
                          {booking.clientName || booking.customer_name || booking.client_name || "Guest"}
                        </h4>
                      </div>
                      <button
                        onClick={() => onViewBooking && onViewBooking(booking)}
                        className="text-xs font-bold text-pink-900 hover:text-pink-950 underline"
                      >
                        Details
                      </button>
                    </div>

                    <div className="flex items-center gap-3 text-[11px] text-gray-600 font-medium">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-pink-700" />
                        <span>{booking.start_time} - {booking.end_time}</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-pink-950 font-bold ml-auto">
                        <span>₹{Number(booking.totalAmount ?? booking.total_rent ?? booking.total_amount ?? 0).toLocaleString("en-IN")}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
}
